import { AnimatePresence, motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';

interface ConsentBannerProps {
  onAccept: () => void;
  onDecline: () => void;
}

export function ConsentBanner({ onAccept, onDecline }: ConsentBannerProps) {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-0 inset-x-0 z-40 border-t border-[#CAC7C7] bg-[#F4F2F2]/95 backdrop-blur-md shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
        role="banner"
        aria-label={t('consent.title')}
      >
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-8 sm:py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Shield className="w-5 h-5 text-[#E94E1B] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#2E2A23] mb-1">{t('consent.title')}</p>
                <p className="text-xs text-[#888185] leading-relaxed">
                  {t('consent.description')}{' '}
                  <a
                    href="/privacy"
                    className="text-[#E94E1B] underline underline-offset-2 hover:text-[#C43D0F]"
                  >
                    {t('consent.privacyLink')}
                  </a>
                  .
                </p>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto flex-shrink-0">
              <Button variant="ghost" size="sm" onClick={onDecline} className="flex-1 sm:flex-none">
                {t('consent.decline')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={onAccept}
                className="flex-1 sm:flex-none"
              >
                {t('consent.accept')}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
